import React, { useState, useEffect, useMemo, startTransition } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  TablePagination,
  Paper,
  Box,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ProjectAdminSummary, ProjectStatusEnum } from '../../../api/Api';
import clientService from '../../../clientService';
import { countryList } from '../../../constants/countryList';
import { useAppState } from 'src/state-management/state-provider';
import { formatDate } from 'src/util/date-utils';

type Order = 'asc' | 'desc';

interface Column {
  id: keyof ProjectAdminSummary | 'actions' | 'assigned_to';
  label: string;
  sortable?: boolean;
  filterable?: boolean;
}

const columns: readonly Column[] = [
  { id: 'project_name', label: 'Name', sortable: true, filterable: true },
  { id: 'assigned_to', label: 'Assigned', sortable: true, filterable: false },
  { id: 'project_developer', label: 'Name', sortable: true, filterable: false },
  { id: 'project_type', label: 'Type', sortable: true, filterable: false },
  { id: 'project_start_date', label: 'Start', sortable: true, filterable: false },
  { id: 'project_end_date', label: 'End', sortable: true, filterable: false },
  { id: 'country', label: 'Country', sortable: true, filterable: false },
  { id: 'created_by', label: 'Created By', sortable: true, filterable: true },
  { id: 'project_status', label: 'Status', sortable: true, filterable: true },
];

const getStatusChip = (status: ProjectStatusEnum | null | undefined) => {
  let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
  switch (status) {
    case ProjectStatusEnum.Draft:
      color = 'default';
      break;
    case ProjectStatusEnum.UnderDevelopment:
      color = 'warning';
      break;
    default:
      break;
  }
  return <Chip label={status || '-'} color={color} size="small" />;
};

const ProjectList: React.FC = () => {
  const { state, dispatch } = useAppState();
  const projects = state.admin.projects;

  const countryMap = useMemo(() => {
    const map = new Map<string, (typeof countryList)[0]>();
    for (const country of countryList) {
      map.set(country.countryCode2, country);
    }
    return map;
  }, []);

  const getCountryName = (countryCode: string | null | undefined): React.ReactNode => {
    if (!countryCode) return '-';
    const country = countryMap.get(countryCode);

    if (country) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            loading="lazy"
            width="20"
            src={`https://flagcdn.com/w20/${country.countryCode2.toLowerCase()}.png`}
            alt={country.name}
            style={{ marginRight: '8px' }}
          />
          {country.name}
        </Box>
      );
    }

    return countryCode; // Fallback for unknown country codes
  };
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof ProjectAdminSummary | 'assigned_to'>('project_name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<Partial<Record<keyof ProjectAdminSummary, string>>>({});
  const navigate = useNavigate();

  const handleRowClick = (projectId: string) => {
    const project = state.admin.projects.find((p) => p.project_id === projectId);
    if (project && project.project_status === ProjectStatusEnum.UnderDevelopment) {
      startTransition(() => {
        navigate(`/admin/project/${projectId}`);
      });
    }
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
        startTransition(() => {
          dispatch({ type: 'ADMIN_SET_PROJECTS', payload: response.data });
        });
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };

    if (projects.length === 0) {
      fetchProjects();
    }
  }, []);

  const handleRequestSort = (property: keyof ProjectAdminSummary | 'assigned_to') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (property: keyof ProjectAdminSummary, value: string) => {
    setFilters({
      ...filters,
      [property]: value,
    });
  };

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      return Object.entries(filters).every(([key, value]) => {
        const filterKey = key as keyof ProjectAdminSummary;
        if (filterKey === 'created_by') {
          return String(project.created_by.first_name).toLowerCase().includes(value.toLowerCase());
        }
        const projectValue = project[filterKey];
        if (projectValue === null || projectValue === undefined) {
          return value === '';
        }
        return String(projectValue).toLowerCase().includes(value.toLowerCase());
      });
    });
  }, [projects, filters]);

  const sortedProjects = useMemo(() => {
    return [...filteredProjects].sort((a, b) => {
      let aValue: string | undefined | null;
      let bValue: string | undefined | null;
      if (orderBy === 'created_by') {
        aValue = a.created_by.first_name;
        bValue = b.created_by.first_name;
      } else if (orderBy === 'assigned_to') {
        aValue = a.assignment ? (a.assignment.user.first_name || '') : '';
        bValue = b.assignment ? (b.assignment.user.first_name || '') : '';
      } else {
        aValue = a[orderBy as keyof ProjectAdminSummary] as string | null | undefined;
        bValue = b[orderBy as keyof ProjectAdminSummary] as string | null | undefined;
      }

      const aStr = (aValue ?? '').toString().toLowerCase();
      const bStr = (bValue ?? '').toString().toLowerCase();

      const cmp = aStr.localeCompare(bStr);
      if (cmp < 0) return order === 'asc' ? -1 : 1;
      if (cmp > 0) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProjects, order, orderBy]);

  const paginatedProjects = useMemo(() => {
    return sortedProjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedProjects, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id as keyof ProjectAdminSummary | 'assigned_to')}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                  {column.filterable && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder={`Filter ${column.label}`}
                        onChange={(e) => handleFilterChange(column.id as keyof ProjectAdminSummary, e.target.value)}
                      />
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProjects.map((project) => (
              <TableRow
                key={project.project_id}
                hover
                onClick={() => handleRowClick(project.project_id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{project.project_name}</TableCell>
                <TableCell>
                  {project.assignment ?
                    `${project.assignment.user.first_name || ''} ${project.assignment.user.last_name || ''}`.trim() || ''
                    : ''
                  }
                </TableCell>
                <TableCell>{project.project_developer}</TableCell>
                <TableCell>{project.project_type}</TableCell>
                <TableCell>{formatDate(project.project_start_date)}</TableCell>
                <TableCell>{formatDate(project.project_end_date)}</TableCell>
                <TableCell>{getCountryName(project.country)}</TableCell>
                <TableCell>{project.created_by.first_name}</TableCell>
                <TableCell>{getStatusChip(project.project_status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredProjects.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default ProjectList;